// This is intended to eventually replace components/ExplainSidebar

import React, { useState } from "react";

import { groupBy, sortBy } from "lodash";

import { describeAnnotation, getAnnotationTitle } from "utils/explain";
import {
  useAnnotationLink,
  useExplainSelection,
  useSelectedAnnotationId,
  useSelectedNode,
} from "components/WithExplainNodeSelection";
import { Annotation } from "types/explain";
import PanelSection from "components/PanelSection";
import ExplainStatsAndOptions from "components/ExplainStatsAndOptions";
import { ComparablePlanType, useCurrentPlan } from "components/WithExplainPlan";

import { ExpandToggle } from "components/Icons";
import Button from "components/Button";
import { Link } from "react-router-dom";
import { useRoutes } from "utils/routes";
import classNames from "classnames";

export default function ExplainSidebarV2({
  databaseId,
  queryId,
  issueId,
  relatedWorkbookId,
  plan,
  blockSize,
  ioBlocks,
}: {
  databaseId: string;
  queryId: string;
  issueId?: string;
  relatedWorkbookId: string;
  plan: ComparablePlanType;
  ioBlocks: number;
  blockSize: number;
}) {
  const { databaseQueryExplain } = useRoutes();

  return (
    <>
      <ExplainStatsAndOptions
        className="mb-2"
        seenAt={plan.seenAt}
        fingerprint={plan.fingerprint}
        totCost={plan.totCost}
        ioBlocks={ioBlocks}
        ioMs={plan.ioMs}
        plan={plan.plan.plan}
        runtime={plan.runtime}
        blockSize={blockSize}
        planLink={
          plan.humanId &&
          databaseQueryExplain(databaseId, queryId, plan.humanId)
        }
      />
      <PanelSection className="-mx-2">
        <ExplainInsights
          databaseId={databaseId}
          queryId={queryId}
          plan={plan}
          issueId={issueId}
          relatedWorkbookId={relatedWorkbookId}
        />
      </PanelSection>
    </>
  );
}

export function ExplainInsights({
  databaseId,
  queryId,
  plan,
  relatedWorkbookId,
  issueId,
}: {
  databaseId: string;
  queryId: string;
  plan: ComparablePlanType;
  relatedWorkbookId: string;
  issueId?: string;
}) {
  const { qaAnnotations, otherAnnotations } = groupBy(
    plan.plan.annotations,
    (ann) => {
      return ann.type.startsWith("query advisor/")
        ? "qaAnnotations"
        : "otherAnnotations";
    },
  );

  const othersByType = groupBy(otherAnnotations, (ann) => ann.type);

  const selectedAnnotationId = useSelectedAnnotationId();
  const hasQueryAdvisorAnnotations = qaAnnotations?.length > 0;
  const hasAnyAnnotations = plan.plan.annotations.length > 0;
  return (
    <>
      <h4 className="text-[#606060]">Insights</h4>
      {/*
          If we have any insights for this plan at all, highlight Query Advisor
          insights explicitly, whether we have them or not. If we have no
          annotations at all, just say we've got nothing, without mentioning
          Query Advisor.
        */}
      {hasAnyAnnotations ? (
        <>
          <h5 className="mb-1 font-semibold text-[#333333]">Query Advisor</h5>
          {hasQueryAdvisorAnnotations ? (
            <>
              <div className="flex flex-col gap-2">
                {qaAnnotations.map((annotation, i) => {
                  return (
                    <AnnotationDisplay
                      databaseId={databaseId}
                      queryId={queryId}
                      issueId={issueId}
                      relatedWorkbookId={relatedWorkbookId}
                      key={i}
                      annotation={annotation}
                    />
                  );
                })}
              </div>
            </>
          ) : (
            <div>No Query Advisor insights found for this plan.</div>
          )}
          {Object.entries(othersByType).map(([type, annotations]) => {
            const defaultExpand =
              !hasQueryAdvisorAnnotations ||
              annotations.some((ann) => ann.id === selectedAnnotationId);

            return (
              <HeuristicInsights
                key={type}
                type={type}
                annotations={annotations}
                defaultExpanded={defaultExpand}
              />
            );
          })}
        </>
      ) : (
        <div>No automated insights found for this plan.</div>
      )}
    </>
  );
}

function HeuristicInsights({
  type,
  annotations,
  defaultExpanded = false,
}: {
  type: string;
  annotations: Annotation[];
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  function handleToggleExpanded() {
    setExpanded((exp) => !exp);
  }

  // If we have multiple annotations of the same type for the same node, this
  // will omit some of them, but that should not happen
  const annotationsByNode = annotations.reduce<Map<number, Annotation>>(
    (map, ann) => {
      ann.nodes.forEach((node) => {
        map.set(node.id, ann);
      });
      return map;
    },
    new Map(),
  );
  const sortedByNode = sortBy(
    Array.from(annotationsByNode.entries()),
    ([nodeId, _anns]) => nodeId,
  );

  return (
    <>
      <Button className="w-full" bare onClick={handleToggleExpanded}>
        <h5 className="mb-1 flex justify-between font-semibold text-[#333333]">
          {getAnnotationTitle(type)}
          <ExpandToggle expanded={expanded} />
        </h5>
      </Button>
      {expanded && (
        <ul className="flex flex-col !ml-0">
          {sortedByNode.map(([nodeId, annotation]) => {
            return (
              <li key={nodeId} className="list-none">
                <LinkedAnnotation nodeId={nodeId} annotationId={annotation.id}>
                  {annotation.detail}
                </LinkedAnnotation>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

function LinkedAnnotation({
  nodeId,
  annotationId,
  children,
}: {
  nodeId: number;
  annotationId: number;
  children: React.ReactNode;
}) {
  const AnnotationLink = useAnnotationLink();

  const [selectedNode] = useSelectedNode();
  const selectedAnnotationId = useSelectedAnnotationId();
  const selection = useExplainSelection();

  const ofSelectedNode =
    selectedNode && selection === "node" && selectedNode?.extra.id === nodeId;

  const isHighlighted = annotationId === selectedAnnotationId || ofSelectedNode;
  const isSelected =
    selection === "annotation" && annotationId === selectedAnnotationId;

  return (
    <AnnotationLink
      annotationId={isSelected ? undefined : annotationId}
      className="flex items-start gap-1"
    >
      <span
        className={classNames(
          "inline-block text-center leading-[17px] font-bold text-white text-[12px] rounded-sm py-0 px-[3px] min-w-[17px]",
          isHighlighted ? "bg-[#337ab7]" : "bg-[#aba8a8]",
        )}
      >
        {nodeId}
      </span>{" "}
      {children}
    </AnnotationLink>
  );
}

function AnnotationDisplay({
  annotation,
  databaseId,
  queryId,
  issueId,
  relatedWorkbookId,
}: {
  annotation: Annotation;
  databaseId?: string;
  queryId?: string;
  issueId?: string;
  relatedWorkbookId?: string;
}) {
  const { databaseWorkbook, databaseWorkbooksNew } = useRoutes();

  const plan = useCurrentPlan();
  const description = describeAnnotation(annotation, plan.plan.plan);
  const nodeId = annotation.nodes.at(0)?.id;

  return (
    <div>
      <LinkedAnnotation nodeId={nodeId} annotationId={annotation.id}>
        {description.title}
      </LinkedAnnotation>
      <div className="!ml-[22px] !my-2">
        <ul className="!ml-[16px]">
          {description.details.map((detailLine, i) => {
            return <li key={i}>{detailLine}</li>;
          })}
        </ul>
        {annotation.type.startsWith("query advisor") &&
          (relatedWorkbookId ? (
            <Link to={databaseWorkbook(databaseId, relatedWorkbookId)}>
              View workbook
            </Link>
          ) : (
            <Link
              className="btn btn-primary"
              to={databaseWorkbooksNew(databaseId, queryId, issueId, plan.id)}
            >
              Create Workbook
            </Link>
          ))}
      </div>
    </div>
  );
}
